import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';

import { warehouseApi } from '../api/warehouse.api';
import { addDays, fmtDate, fmtQty, norm, todayJakarta } from '../format';
import { useClientCatalog } from '../hooks/useCatalog';
import { useServerErrors } from '../hooks/useServerErrors';
import { useWizard } from '../hooks/useWizard';
import { buildHubClientSnapshot, buildHubSnapshot } from '../snapshot';
import {
  OUTBOUND_METHOD_LABEL,
  OUTBOUND_SERVICE_LABEL,
  OUTBOUND_TYPE_META,
  OUTBOUND_TYPES,
  serviceLabel,
} from '../status';
import type {
  ClientRef,
  CreateOutboundOrderPayload,
  CreateOutboundOrderResult,
  FieldError,
  OutboundOrderType,
  OutboundServiceType,
} from '../types';
import { useWarehouse } from '../WarehouseContext';
import { DateField } from './DateField';
import { EnumSelect, FormField, TextArea, TextInput } from './form';
import OutboundDestinationStep, {
  type DestinationDraft,
  emptyDestination,
} from './OutboundDestinationStep';
import { ClientSelect, HubSelect } from './pickers';
import {
  filledSkuLines,
  newSkuLine,
  parseQty,
  type SkuLineDraft,
  SkuLinesEditor,
} from './SkuLinesEditor';
import { codeCls, KVGrid, metaCls } from './ui';
import { WizardFooter, WizardSteps } from './wizard';

/**
 * Create outbound request (manual), as a 4-step wizard: Order → Penerima &
 * tujuan → Produk → Review. The server's OutboundOrderValidator is the single
 * source of truth (form, CSV and API share it): each step only guards required
 * input before moving on, and a 422 shows EVERY `errors` item at once — in a
 * summary alert and inline at the field — and jumps back to the earliest step
 * that has one.
 *
 * Type TRANSFER ships to another hub (picked on the Order step); submitting
 * its shipping births a TRANSFER PO at that hub. The recipient / destination
 * step follows the client's destination config (OutboundDestinationStep);
 * a transfer types it by hand.
 */

type FieldKey =
  | 'type'
  | 'hub'
  | 'destinationHub'
  | 'client'
  | 'externalID'
  | 'expectedDate'
  | 'serviceType'
  | 'notes'
  | 'recipientName'
  | 'recipientPhone'
  | 'address'
  | 'latitude'
  | 'longitude'
  | 'destNotes'
  | 'city'
  | 'lines'
  | 'form';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (result: CreateOutboundOrderResult) => void;
  initialHubID?: number | null;
  initialClient?: ClientRef | null;
}

// Same day only for now: Next day stays listed but cannot be picked (the
// API / CSV still accept it) — ops asked to switch it on later.
const SERVICE_OPTIONS = (
  Object.keys(OUTBOUND_SERVICE_LABEL) as OutboundServiceType[]
).map((value) => ({
  value,
  label: OUTBOUND_SERVICE_LABEL[value],
  disabled: value === 'NEXT_DAY',
}));

const METHOD_OPTIONS = [
  { value: 'DELIVERY', label: OUTBOUND_METHOD_LABEL.DELIVERY },
];

/** Map a backend field name (snake, dotted or camel) onto a form field. */
export const fieldKeyOf = (
  e: FieldError
): { key: FieldKey; line: number | null; lineField?: 'sku' | 'qty' } => {
  const raw = e.field || '';
  const n = norm(raw);
  if (!n) return { key: 'form', line: null };
  const lineMatch = /lines?\W*(\d+)\W*(sku|qty)?/i.exec(raw);
  const lineIndex =
    e.lineIndex !== undefined && e.lineIndex !== null
      ? e.lineIndex
      : lineMatch
        ? Number(lineMatch[1])
        : null;
  if (
    lineIndex !== null ||
    n.startsWith('line') ||
    n === 'sku' ||
    n === 'qty'
  ) {
    const lineField = n.endsWith('qty')
      ? 'qty'
      : n.endsWith('sku')
        ? 'sku'
        : undefined;
    return { key: 'lines', line: lineIndex, lineField };
  }
  // Before `hub` / `destination…`: destinationHubID|Name|Code.
  if (n.includes('destinationhub'))
    return { key: 'destinationHub', line: null };
  if (n.includes('cityid') || n.includes('destinationcity'))
    return { key: 'city', line: null };
  if (n === 'type') return { key: 'type', line: null };
  if (n.includes('phone')) return { key: 'recipientPhone', line: null };
  // The generated hub → destination distance: shown at the address.
  if (n.includes('distance')) return { key: 'address', line: null };
  if (n.includes('recipient') || n === 'name')
    return { key: 'recipientName', line: null };
  if (n.includes('latitude') || n === 'lat')
    return { key: 'latitude', line: null };
  if (n.includes('longitude') || n === 'lng' || n === 'long')
    return { key: 'longitude', line: null };
  if (n.includes('destinationnotes')) return { key: 'destNotes', line: null };
  if (n.includes('address')) return { key: 'address', line: null };
  if (n.includes('expected') || n.includes('date'))
    return { key: 'expectedDate', line: null };
  if (n.includes('service')) return { key: 'serviceType', line: null };
  if (n.includes('external')) return { key: 'externalID', line: null };
  if (n.includes('hub')) return { key: 'hub', line: null };
  if (n.includes('client')) return { key: 'client', line: null };
  if (n === 'notes') return { key: 'notes', line: null };
  return { key: 'form', line: null };
};

const STEPS = [
  { id: 1, label: 'Order' },
  { id: 2, label: 'Penerima & tujuan' },
  { id: 3, label: 'Produk' },
  { id: 4, label: 'Review' },
];

const STEP_OF: Record<FieldKey, number> = {
  type: 1,
  hub: 1,
  destinationHub: 1,
  client: 1,
  externalID: 1,
  expectedDate: 1,
  serviceType: 1,
  notes: 1,
  recipientName: 2,
  recipientPhone: 2,
  address: 2,
  latitude: 2,
  longitude: 2,
  destNotes: 2,
  city: 2,
  lines: 3,
  form: 4,
};

const stepOfKey = (k: FieldKey): number => STEP_OF[k];

/** The wizard step a (server or local) error belongs to. */
export const stepOfError = (e: FieldError): number =>
  stepOfKey(fieldKeyOf(e).key);

const toNumber = parseQty;

const OutboundRequestFormModal: React.FC<Props> = ({
  open,
  onClose,
  onCreated,
  initialHubID = null,
  initialClient = null,
}) => {
  const { hubs } = useWarehouse();
  const [type, setType] = useState<OutboundOrderType>('REGULAR');
  const [hubID, setHubID] = useState<number | null>(initialHubID);
  const [destHubID, setDestHubID] = useState<number | null>(null);
  const [client, setClient] = useState<ClientRef | null>(initialClient);
  const [externalID, setExternalID] = useState('');
  const [expectedDate, setExpectedDate] = useState(addDays(todayJakarta(), 1));
  const [serviceType, setServiceType] =
    useState<OutboundServiceType>('SAME_DAY');
  const [notes, setNotes] = useState('');
  const [destination, setDestination] =
    useState<DestinationDraft>(emptyDestination);
  const patchDestination = useCallback(
    (patch: Partial<DestinationDraft>) =>
      setDestination((d) => ({ ...d, ...patch })),
    []
  );
  const {
    recipientName,
    recipientPhone,
    address,
    latitude,
    longitude,
    destNotes,
  } = destination;
  const [lines, setLines] = useState<SkuLineDraft[]>(() => [newSkuLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const server = useServerErrors(fieldKeyOf, stepOfKey);
  const { errors, setErrors, fieldError: fe } = server;
  /**
   * The ref client must be unique per client: asked on blur (and before the
   * step moves on), the answer names the order holding it. `for` is the
   * (client, ref) pair the answer is about, so a stale one is never shown.
   */
  const [refTaken, setRefTaken] = useState<{
    for: string;
    code: string;
  } | null>(null);
  const refCheck = useRef<AbortController | null>(null);
  useEffect(() => () => refCheck.current?.abort(), []);
  const refKey = `${client?.id ?? ''}|${externalID.trim()}`;
  const refTakenNow = refTaken && refTaken.for === refKey ? refTaken : null;
  /** The code of the order already holding the ref, or null. */
  const checkExternalId = useCallback(async (): Promise<string | null> => {
    const ref = externalID.trim();
    if (!client || !ref) return null;
    const key = `${client.id}|${ref}`;
    if (refTaken?.for === key) return refTaken.code;
    refCheck.current?.abort();
    const controller = new AbortController();
    refCheck.current = controller;
    try {
      const res = await warehouseApi.checkOutboundExternalId(client.id, ref, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return null;
      if (res?.exists && res.order) {
        setRefTaken({ for: key, code: res.order.code });
        return res.order.code;
      }
      return null;
    } catch {
      // Unknown: the server refuses a taken ref at create anyway (409).
      return null;
    }
  }, [client, externalID, refTaken]);

  const scrollTop = useCallback(() => {
    const el =
      bodyRef.current?.closest('[class*="overflow-y-auto"]') ?? bodyRef.current;
    el?.scrollTo?.({ top: 0 });
  }, []);

  const transfer = type === 'TRANSFER';
  const filledLines = filledSkuLines(lines);

  /** Required-input guard for one step; the server does the real validation. */
  const localErrorsOf = (s: number): FieldError[] => {
    const out: FieldError[] = [];
    if (s === 1) {
      if (!hubID) out.push({ field: 'hub', message: 'Hub wajib dipilih.' });
      if (transfer) {
        if (!destHubID)
          out.push({
            field: 'destinationHubID',
            message: 'Hub tujuan wajib dipilih.',
          });
        else if (destHubID === hubID)
          out.push({
            field: 'destinationHubID',
            message: 'Hub tujuan harus beda dari hub asal.',
          });
      }
      if (!client)
        out.push({ field: 'client', message: 'Client wajib dipilih.' });
      if (!expectedDate)
        out.push({
          field: 'expectedDeliveryDate',
          message: 'Expected delivery wajib diisi.',
        });
      else if (expectedDate < todayJakarta())
        out.push({
          field: 'expectedDeliveryDate',
          message: 'Expected delivery tidak boleh di masa lalu.',
        });
    }
    if (s === 2) {
      if (!recipientName.trim())
        out.push({
          field: 'recipient.name',
          message: 'Nama penerima wajib diisi.',
        });
      if (!recipientPhone.trim())
        out.push({ field: 'recipient.phone', message: 'Telepon wajib diisi.' });
      if (!address.trim())
        out.push({
          field: 'destination.address',
          message: 'Alamat tujuan wajib diisi.',
        });
      const lat = toNumber(latitude);
      const lng = toNumber(longitude);
      if (lat === null || lat < -90 || lat > 90)
        out.push({
          field: 'destination.latitude',
          message: 'Latitude harus angka -90…90.',
        });
      if (lng === null || lng < -180 || lng > 180)
        out.push({
          field: 'destination.longitude',
          message: 'Longitude harus angka -180…180.',
        });
    }
    if (s === 3) {
      if (!filledLines.length)
        out.push({
          field: 'lines',
          message: 'Minimal satu produk.',
          lineIndex: 0,
        });
      lines.forEach((l, i) => {
        if (!l.sku && !l.qty.trim()) return;
        const q = toNumber(l.qty);
        if (!l.sku)
          out.push({
            field: 'sku',
            message: 'SKU wajib dipilih.',
            lineIndex: i,
          });
        else if (q === null || q <= 0)
          out.push({ field: 'qty', message: 'Qty harus > 0.', lineIndex: i });
      });
    }
    return out;
  };

  const wizard = useWizard<FieldError>({
    total: STEPS.length,
    check: localErrorsOf,
    onMove: scrollTop,
  });
  const { step } = wizard;
  const resetWizard = wizard.reset;

  useEffect(() => {
    if (!open) return;
    resetWizard();
    setType('REGULAR');
    setHubID(initialHubID);
    setDestHubID(null);
    setClient(initialClient);
    setExternalID('');
    setExpectedDate(addDays(todayJakarta(), 1));
    setServiceType('SAME_DAY');
    setNotes('');
    setDestination(emptyDestination());
    setLines([newSkuLine()]);
    setErrors([]);
    setFormError(null);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const catalog = useClientCatalog({
    clientID: client?.id ?? null,
    hubID,
    chosen: lines.map((l) => l.sku),
    enabled: open,
  });

  const shortages = useMemo(() => {
    const totals = new Map<string, number>();
    lines.forEach((l) => {
      const q = toNumber(l.qty);
      if (l.sku && q && q > 0) totals.set(l.sku, (totals.get(l.sku) ?? 0) + q);
    });
    return Array.from(totals.entries())
      .map(([sku, requested]) => ({
        sku,
        requested,
        available: catalog.availableFor(sku),
      }))
      .filter(
        (x): x is { sku: string; requested: number; available: number } =>
          x.available !== null && x.requested > x.available
      );
  }, [lines, catalog]);

  if (!open) return null;

  const hub = hubs.find((h) => h.id === hubID);
  const destHub = hubs.find((h) => h.id === destHubID);
  const destHubName = destHub?.name ?? (destHubID ? `Hub #${destHubID}` : null);

  const refTakenError = (code: string): FieldError => ({
    field: 'externalID',
    message: `Ref client sudah dipakai order ${code}.`,
  });

  const next = () => {
    const local = wizard.next();
    // Keep errors of other steps (e.g. from a 422) until their step is passed.
    setErrors((prev) => [
      ...prev.filter((e) => stepOfError(e) !== step),
      ...local,
    ]);
    setFormError(null);
    if (local.length) scrollTop();
  };

  /** Order step: also ask the server about the ref before moving on. */
  const nextFromOrder = async () => {
    if (step === 1 && externalID.trim() && client) {
      const code = await checkExternalId();
      if (code) {
        setErrors((prev) => [
          ...prev.filter((e) => fieldKeyOf(e).key !== 'externalID'),
          refTakenError(code),
        ]);
        scrollTop();
        return;
      }
    }
    next();
  };

  const back = () => {
    setFormError(null);
    wizard.back();
  };

  /** Validate every step, then build the payload — typed, no assertions. */
  const validate = ():
    | { payload: CreateOutboundOrderPayload; errors: null }
    | { payload: null; errors: FieldError[] } => {
    const local = wizard.checkAll();
    if (refTakenNow) local.push(refTakenError(refTakenNow.code));
    if (local.length || !hubID || !client || (transfer && !destHubID)) {
      return { payload: null, errors: local };
    }
    const dest =
      transfer && destHubID !== null ? buildHubSnapshot(hubs, destHubID) : null;
    return {
      errors: null,
      payload: {
        type,
        ...(dest
          ? {
              destinationHubID: dest.hubID,
              destinationHubName: dest.hubName,
              ...(dest.hubCode ? { destinationHubCode: dest.hubCode } : {}),
            }
          : {}),
        ...buildHubClientSnapshot(hubs, hubID, client),
        ...(externalID.trim() ? { externalID: externalID.trim() } : {}),
        serviceType,
        expectedDeliveryDate: expectedDate,
        recipient: {
          name: recipientName.trim(),
          phone: recipientPhone.trim(),
        },
        destination: {
          address: address.trim(),
          latitude: toNumber(latitude),
          longitude: toNumber(longitude),
          notes: destNotes.trim(),
          // The city shown in the form (picked or auto-detected) is what gets
          // stored; none shown → the server resolves it again on create.
          ...(destination.city ? { cityID: destination.city.id } : {}),
        },
        lines: filledLines.map((l) => ({ sku: l.sku, qty: toNumber(l.qty) })),
        notes: notes.trim(),
      },
    };
  };

  const handleSubmit = async () => {
    const { payload, errors: local } = validate();
    if (!payload) {
      setErrors(local);
      scrollTop();
      return;
    }
    setSubmitting(true);
    setErrors([]);
    setFormError(null);
    try {
      const res = await warehouseApi.createOutboundOrder(payload);
      onCreated(res);
    } catch (err) {
      const shown = server.fromError(err);
      if (shown.step !== null) wizard.goTo(shown.step);
      else setFormError(shown.message);
      scrollTop();
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabel = (e: FieldError) => STEPS[stepOfError(e) - 1]?.label;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon="ri-add-line"
      title="Create outbound request"
      width="xl"
      confirmOnClose={!submitting}
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <WizardFooter
          step={step}
          total={STEPS.length}
          onCancel={onClose}
          onBack={back}
          onNext={() => void nextFromOrder()}
          onSubmit={() => void handleSubmit()}
          submitLabel="Create outbound request"
          submitTestId="oo-submit"
          busy={submitting}
        />
      }
    >
      <div ref={bodyRef} className="flex flex-col gap-5">
        <WizardSteps steps={STEPS} current={step} />
        {errors.length > 0 && (
          <Alert
            status="error"
            appearance="stroke"
            size="lg"
            title={`${errors.length} isian perlu diperbaiki`}
            data-testid="oo-errors"
          >
            <ul className="mt-1 list-disc pl-4">
              {errors.map((e, i) => (
                <li key={`${i}-${e.message}`}>
                  {stepOfError(e) !== step && (
                    <span className="text-text-sub-600">{stepLabel(e)} · </span>
                  )}
                  {e.message}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {formError && (
          <Alert
            status="error"
            appearance="stroke"
            size="lg"
            title="Gagal menyimpan"
          >
            {formError}
          </Alert>
        )}

        {step === 1 && (
          <div className="flex flex-col gap-3">
            <FormField label="Tipe" required error={fe('type')}>
              <SegmentedControl
                value={type}
                onValueChange={(v: string) => {
                  if (v === 'REGULAR' || v === 'TRANSFER') setType(v);
                }}
                aria-label="Tipe outbound"
                className="w-full [&>*]:flex-1"
              >
                {OUTBOUND_TYPES.map((t) => (
                  <SegmentedItem key={t} value={t} data-testid={`oo-type-${t}`}>
                    <i
                      className={`${OUTBOUND_TYPE_META[t].icon} text-[14px]`}
                    />
                    {OUTBOUND_TYPE_META[t].label}
                  </SegmentedItem>
                ))}
              </SegmentedControl>
            </FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                label={transfer ? 'Hub asal' : 'Hub'}
                required
                error={fe('hub')}
              >
                <HubSelect
                  value={hubID}
                  onChange={setHubID}
                  invalid={!!fe('hub')}
                />
              </FormField>
              <FormField
                label="Client"
                required
                error={fe('client')}
                testId="oo-client"
              >
                <ClientSelect
                  value={client}
                  onChange={(c) => {
                    // Another client → another catalog and destination book.
                    if (c?.id !== client?.id) {
                      setDestination(emptyDestination());
                      setErrors((prev) =>
                        prev.filter((e) => stepOfError(e) !== 2)
                      );
                    }
                    setClient(c);
                    setLines([newSkuLine()]);
                  }}
                  invalid={!!fe('client')}
                />
              </FormField>
            </div>
            {transfer && (
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  label="Hub tujuan"
                  required
                  error={fe('destinationHub')}
                  testId="oo-dest-hub"
                >
                  <HubSelect
                    value={destHubID}
                    onChange={setDestHubID}
                    exclude={[hubID]}
                    placeholder="Pilih hub tujuan"
                    invalid={!!fe('destinationHub')}
                  />
                </FormField>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <FormField
                label="Ref client / external ID"
                htmlFor="oo-ext"
                optional
                error={
                  fe('externalID') ??
                  (refTakenNow
                    ? `Ref client sudah dipakai order ${refTakenNow.code}.`
                    : null)
                }
              >
                <TextInput
                  id="oo-ext"
                  size="md"
                  value={externalID}
                  onChange={(v) => {
                    setExternalID(v);
                    setErrors((prev) =>
                      prev.filter((e) => fieldKeyOf(e).key !== 'externalID')
                    );
                  }}
                  onBlur={() => void checkExternalId()}
                  invalid={!!fe('externalID') || !!refTakenNow}
                  className="font-mono"
                />
              </FormField>
              <FormField
                label="Expected delivery"
                htmlFor="oo-date"
                required
                error={fe('expectedDate')}
              >
                <DateField
                  id="oo-date"
                  value={expectedDate}
                  onChange={setExpectedDate}
                  invalid={!!fe('expectedDate')}
                />
              </FormField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField
                label="Service"
                htmlFor="oo-service"
                error={fe('serviceType')}
                hint="Sementara hanya Same day — Next day belum dibuka."
              >
                <EnumSelect
                  id="oo-service"
                  value={serviceType}
                  onChange={(v) => setServiceType(v as OutboundServiceType)}
                  options={SERVICE_OPTIONS}
                />
              </FormField>
              <FormField label="Method" htmlFor="oo-method">
                <EnumSelect
                  id="oo-method"
                  value="DELIVERY"
                  onChange={() => undefined}
                  options={METHOD_OPTIONS}
                  disabled
                />
              </FormField>
            </div>
            <FormField
              label="Catatan order"
              htmlFor="oo-notes"
              optional
              error={fe('notes')}
            >
              <TextArea
                id="oo-notes"
                value={notes}
                onChange={setNotes}
              />
            </FormField>
          </div>
        )}

        {step === 2 && (
          <OutboundDestinationStep
            client={client}
            transfer={transfer}
            value={destination}
            onChange={patchDestination}
            fieldError={fe}
          />
        )}

        {step === 3 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="label-eyebrow">Produk · {client?.name}</p>
              <Button
                tone="neutral"
                style="stroke"
                size="xs"
                onClick={() => setLines((prev) => [...prev, newSkuLine()])}
                leftIcon={<i className="ri-add-line text-[14px]" />}
              >
                Tambah baris
              </Button>
            </div>
            {catalog.error && (
              <Alert
                status="error"
                appearance="stroke"
                size="sm"
                title={`Katalog SKU gagal dimuat: ${catalog.error.message}`}
                data-testid="catalog-error"
                action={
                  <Button
                    tone="neutral"
                    style="stroke"
                    size="xs"
                    onClick={catalog.reload}
                  >
                    Coba lagi
                  </Button>
                }
              />
            )}
            <SkuLinesEditor
              lines={lines}
              setLines={setLines}
              catalog={catalog}
              showAvailability
              selectDisabled={!client}
              selectPlaceholder={
                !client
                  ? 'Pilih client dulu'
                  : catalog.error
                    ? 'Katalog gagal dimuat'
                    : 'Pilih SKU'
              }
              lineError={server.lineError}
              qtyMin={0}
              qtyStep="any"
              qtyPlaceholder="0"
              removeSize="icon-xs"
            />
          </div>
        )}

        {step === 4 && (
          <div className="flex flex-col gap-4" data-testid="oo-review">
            <div>
              <p className="label-eyebrow">Order</p>
              <KVGrid
                items={[
                  ['Tipe', OUTBOUND_TYPE_META[type].label],
                  [
                    transfer ? 'Hub asal' : 'Hub',
                    hub?.name ?? (hubID ? `Hub #${hubID}` : null),
                  ],
                  ...(transfer
                    ? ([['Hub tujuan', destHubName]] as [
                        string,
                        React.ReactNode,
                      ][])
                    : []),
                  ['Client', client?.name],
                  ['Ref client', externalID.trim() || 'Otomatis (request ID)'],
                  ['Expected delivery', fmtDate(expectedDate)],
                  ['Service', serviceLabel(serviceType)],
                  ['Catatan', notes.trim() || null],
                ]}
              />
            </div>
            <div>
              <p className="label-eyebrow">Penerima &amp; tujuan</p>
              <KVGrid
                items={[
                  [
                    'Penerima',
                    `${recipientName.trim()} · ${recipientPhone.trim()}`,
                  ],
                  ['Alamat', address.trim()],
                  [
                    'Koordinat',
                    <span key="c" className="font-mono">
                      {latitude.trim()}, {longitude.trim()}
                    </span>,
                  ],
                  ['Catatan tujuan', destNotes.trim() || null],
                ]}
              />
            </div>
            <div className="flex flex-col gap-2">
              <p className="label-eyebrow">Produk</p>
              <div className="flex flex-col divide-y divide-stroke-soft-200 rounded-lg border border-stroke-soft-200">
                {filledLines.map((l) => {
                  const s = catalog.skuBy.get(l.sku);
                  const avail = catalog.availableFor(l.sku);
                  return (
                    <div
                      key={l.key}
                      className="flex items-center justify-between gap-3 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className={codeCls}>{l.sku}</p>
                        <p className={`${metaCls} truncate`}>{s?.itemName}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[13px] tabular-nums text-text-strong-950">
                          {fmtQty(toNumber(l.qty) ?? 0)} {s?.uomName}
                        </p>
                        {avail !== null && (
                          <p className={metaCls}>tersedia {fmtQty(avail)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {(step === 3 || step === 4) && shortages.length > 0 && (
          <Alert
            status="warning"
            appearance="stroke"
            size="lg"
            title="Stok belum cukup"
          >
            {shortages.map((x, i) => (
              <span key={x.sku}>
                {i > 0 && ' · '}
                <span className="font-mono">{x.sku}</span> butuh{' '}
                {fmtQty(x.requested)}, tersedia {fmtQty(x.available)}
              </span>
            ))}
            .
          </Alert>
        )}
      </div>
    </Modal>
  );
};

export default OutboundRequestFormModal;
